import React, { useEffect, useState } from 'react';
import { typeApi } from '../services/api';
import type { TypeRules } from '../types';
import './TypeManager.css';

/**
 * TypeManager component - Manages node and relationship type definitions
 * Allows admins to define new types and validation rules
 */
interface TypeManagerProps {
  isAdmin: boolean;
  onUpdated?: () => void;
}

const TypeManager: React.FC<TypeManagerProps> = ({ isAdmin, onUpdated }) => {
  const [rules, setRules] = useState<TypeRules | null>(null);
  const [draftJson, setDraftJson] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    loadTypes();
  }, []);

  /**
   * Load all type definitions
   */
  const loadTypes = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const loaded = await typeApi.getTypeRules();
      const unwrapped = (loaded as unknown as { data?: TypeRules }).data ?? loaded;
      setRules(unwrapped);
      setDraftJson(JSON.stringify(unwrapped, null, 2));
      setLoading(false);
    } catch (err) {
      console.error('Failed to load types:', err);
      setError((err as Error).message || 'Failed to load type definitions');
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="type-manager"><div className="loading">Loading types...</div></div>;
  }

  const handleSave = async (): Promise<void> => {
    if (!isAdmin) return;
    setSaveMessage(null);
    setSaving(true);
    try {
      const parsed = JSON.parse(draftJson) as TypeRules;
      await typeApi.updateTypeRules(parsed);
      setRules(parsed);
      setSaveMessage('Schema rules updated.');
      onUpdated?.();
    } catch (err) {
      setSaveMessage((err as Error).message || 'Failed to save schema rules.');
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="type-manager">
        <div className="error">
          <p>Error: {error}</p>
          <button onClick={loadTypes}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="type-manager">
      <h2>Schema Rules Editor</h2>

      {rules && (
        <p className="description" style={{ marginBottom: 10 }}>
          Editing schema version {rules.version} ({rules.node_types.length} node types, {rules.relationship_types.length} relationship types).
        </p>
      )}

      <div className="type-list">
        <textarea
          value={draftJson}
          onChange={(e) => setDraftJson(e.target.value)}
          rows={20}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
          aria-label="Schema JSON editor"
          disabled={!isAdmin || saving}
        />
      </div>

      <div className="actions">
        <button className="btn-primary" onClick={handleSave} disabled={!isAdmin || saving}>
          {saving ? 'Saving…' : 'Save Schema Rules'}
        </button>
        {!isAdmin && <span className="description" style={{ marginLeft: 10 }}>Admin role required</span>}
      </div>

      {saveMessage && (
        <div className="actions" style={{ marginTop: 8 }}>
          <span className="description">{saveMessage}</span>
        </div>
      )}
    </div>
  );
};

export default TypeManager;
